<template>
  <div class="rm-screen">
    <div v-if="!employee" class="rm-empty">{{ $t("no_employee_report_data") }}</div>

    <template v-else>
      <div class="rm-month-nav">
        <button
          type="button"
          class="rm-icon-btn"
          :aria-label="$t('report_mobile.prev_month')"
          @click="stepMonth(-1)"
        >
          <v-icon size="20" color="#1c1917">mdi-chevron-left</v-icon>
        </button>
        <button type="button" class="rm-month-nav__title" @click="monthSheet = true">
          <span class="rm-month-nav__name">{{ monthTitle }}</span>
          <span v-if="monthHint" class="rm-muted rm-month-nav__hint">{{ monthHint }}</span>
        </button>
        <button
          type="button"
          class="rm-icon-btn"
          :aria-label="$t('report_mobile.month_next')"
          @click="stepMonth(1)"
        >
          <v-icon size="20" color="#1c1917">mdi-chevron-right</v-icon>
        </button>
      </div>

      <div class="rm-stats rm-stats--pair">
        <div class="rm-stat rm-stat--inverse">
          <span class="rm-stat__label">{{ $t("report_mobile.stat_orders") }}</span>
          <span class="rm-stat__value rm-num">{{ totals.orders }}</span>
        </div>
        <div class="rm-stat">
          <span class="rm-stat__label">{{ $t("report_mobile.stat_dishes") }}</span>
          <span class="rm-stat__value rm-num">{{ totals.dishes }}</span>
        </div>
      </div>

      <div v-if="showRestaurantFilter" class="rm-chip-row rm-emp-chips" role="group" :aria-label="$t('restaurants')">
        <button
          v-for="name in restaurantNames"
          :key="name"
          type="button"
          class="rm-chip"
          :class="{ 'rm-chip--active': selectedRestaurants.includes(name) }"
          :aria-pressed="String(selectedRestaurants.includes(name))"
          @click="toggleRestaurant(name)"
        >
          {{ name }}
        </button>
      </div>

      <label class="rm-switch-row">
        {{ $t("show_all_days") }}
        <input v-model="showAllDays" type="checkbox" role="switch" />
      </label>

      <div v-if="loading" class="rm-empty">
        <v-progress-circular indeterminate color="#c2410c"></v-progress-circular>
      </div>
      <div v-else-if="failed" class="rm-empty">
        {{ $t("report_mobile.load_failed") }}
        <button type="button" class="rm-btn rm-btn--sm rm-emp-retry" @click="loadMonth">
          {{ $t("report_mobile.retry") }}
        </button>
      </div>
      <ul v-else-if="days.length" class="rm-list rm-card">
        <li v-for="day in days" :key="day.key" class="rm-day">
          <span class="rm-day__tile" :class="day.tileClass">
            <span class="rm-day__num rm-num">{{ day.num }}</span>
            <span class="rm-day__weekday">{{ day.weekday }}</span>
          </span>
          <span class="rm-day__text">
            <template v-if="day.items.length">
              <span v-for="item in day.items" :key="item.key" class="rm-day__meal">
                {{ item.quantity }}× {{ item.name }}
              </span>
              <span class="rm-muted">{{ day.detail }}</span>
            </template>
            <span v-else class="rm-day__none">{{ day.emptyText }}</span>
          </span>
        </li>
      </ul>
      <div v-else class="rm-empty">{{ $t("report_mobile.no_orders_month") }}</div>
    </template>

    <report-mobile-month-sheet
      v-model="monthSheet"
      :month="month"
      @select="selectMonth"
    ></report-mobile-month-sheet>
  </div>
</template>

<script>
import hasEmployeeMonth from "../../shared/hasEmployeeMonth";
import ReportMobileMonthSheet from "../ReportMobileMonthSheet.vue";

/**
 * Mobilni "Radnik – mesečni pregled": šta je radnik naručio po danima u izabranom mesecu.
 * Radnik (i firma) dolaze iz store-a (report/selectedEmployee), mesec iz kraja izabranog perioda. Kad radnik jede u
 * više restorana, čipovi restorana sužavaju brojke i spisak na izabrane restorane.
 */
export default {
  name: "ReportMobileEmployee",
  mixins: [hasEmployeeMonth],
  components: { ReportMobileMonthSheet },
  data: () => ({
    monthSheet: false,
  }),
};
</script>

<style lang="scss">
.rm-theme .rm-month-nav {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: var(--rm-surface);
  border: 1px solid var(--rm-border);
  border-radius: 14px;
  padding: 2px;

  &__title {
    flex: 1;
    min-width: 0;
    min-height: 44px;
    border: none;
    background: transparent;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: var(--rm-text);
  }

  &__name {
    font-size: 16px;
    font-weight: 700;
  }

  &__hint {
    font-size: 12px;
  }
}

.rm-theme .rm-switch-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 44px;
  padding: 0 4px;
  font-size: 15px;
  cursor: pointer;

  input {
    width: 22px;
    height: 22px;
    margin: 0;
  }
}

/* izbor restorana (radnik jede u više njih): red čipova počinje u liniji kartica i skroluje do ivice */
.rm-theme .rm-emp-chips {
  margin: 0 -16px;
  padding: 0 16px;
}

.rm-theme .rm-emp-retry {
  width: auto;
  padding: 0 20px;
}

.rm-day {
  display: flex;
  align-items: center;
  gap: 14px;
  min-height: 60px;
  padding: 8px 14px;
  box-sizing: border-box;
  border-bottom: 1px solid var(--rm-divider-soft);

  &:last-child {
    border-bottom: none;
  }

  &__tile {
    width: 48px;
    height: 48px;
    flex-shrink: 0;
    border-radius: 12px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: var(--rm-chip-bg);
    color: var(--rm-chip-text);

    &.is-ordered {
      background: var(--rm-accent-soft);
      color: var(--rm-accent-strong);
    }

    &.is-today {
      background: var(--rm-accent);
      color: #ffffff;
    }
  }

  &__num {
    font-size: 18px;
    font-weight: 700;
    line-height: 1.1;
  }

  &__weekday {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
  }

  &__text {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  &__meal {
    font-size: 15px;
    font-weight: 600;
  }

  &__none {
    font-size: 14px;
    color: var(--rm-muted);
  }
}
</style>
